import React, { useEffect, useRef, useState } from 'react';
import { Bell, X, Clock, Clock3 } from 'lucide-react';
import { clockSounds } from '@/utils/clockSounds';

interface TimerAlarmNotificationProps {
  type: 'timer' | 'alarm';
  label?: string;
  onDismiss: () => void;
  onSnooze?: () => void; // Only for alarms
  accentColor: string;
}

export const TimerAlarmNotification: React.FC<TimerAlarmNotificationProps> = ({
  type,
  label,
  onDismiss,
  onSnooze,
  accentColor
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const stopSoundRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 50);

    // Start playing sound
    const soundType = type === 'timer' ? 'timer-complete' : 'gentle-chime';
    stopSoundRef.current = clockSounds.playAlarm(soundType, 3000);

    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(
        type === 'timer' ? '⏱️ Timer Complete!' : '⏰ Alarm!',
        {
          body: label || (type === 'timer' ? 'Your timer has finished' : 'Time to wake up'),
          icon: '/icon-192x192.png',
          tag: `clock-${type}`,
          requireInteraction: true
        }
      );
    }

    return () => {
      // Stop sound when component unmounts
      if (stopSoundRef.current) {
        stopSoundRef.current();
      }
    };
  }, [type, label]);

  const handleDismiss = () => {
    if (stopSoundRef.current) {
      stopSoundRef.current();
    }
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  const handleSnooze = () => {
    if (stopSoundRef.current) {
      stopSoundRef.current();
    }
    setIsVisible(false);
    setTimeout(() => onSnooze?.(), 300);
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-lg transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleDismiss}
    >
      <div
        className={`relative bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-12 max-w-md w-full mx-4 transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated ring pulse effect */}
        <div 
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${accentColor}20 0%, transparent 70%)`,
            animation: 'pulse 2s ease-in-out infinite'
          }}
        />

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center relative"
            style={{
              background: `radial-gradient(circle, ${accentColor}30 0%, ${accentColor}10 100%)`,
              boxShadow: `0 0 40px ${accentColor}40, 0 0 80px ${accentColor}20`
            }}
          >
            {type === 'timer' ? (
              <Clock className="w-12 h-12" style={{ color: accentColor }} />
            ) : (
              <Bell className="w-12 h-12 animate-bounce" style={{ color: accentColor }} />
            )}
            
            {/* Ripple effect */}
            <div
              className="absolute inset-0 rounded-full animate-ping opacity-75"
              style={{
                border: `2px solid ${accentColor}`,
                animationDuration: '2s'
              }}
            />
          </div>
        </div>

        {/* Title */}
        <h2
          className="text-3xl font-bold text-center mb-3 font-clash-display"
          style={{
            color: accentColor,
            textShadow: `0 0 30px ${accentColor}40`
          }}
        >
          {type === 'timer' ? '⏱️ Time\'s Up!' : '⏰ Alarm!'}
        </h2>

        {/* Label */}
        {label && (
          <p className="text-center text-white/80 text-lg mb-8 font-medium">
            {label}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {type === 'alarm' && onSnooze && (
            <button
              onClick={handleSnooze}
              className="flex-1 px-6 py-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2 group"
            >
              <Clock3 className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              Snooze 5m
            </button>
          )}
          
          <button
            onClick={handleDismiss}
            className="flex-1 px-6 py-4 rounded-xl font-semibold transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2 group"
            style={{
              background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}CC 100%)`,
              boxShadow: `0 4px 20px ${accentColor}40`,
              color: '#ffffff'
            }}
          >
            <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
            Dismiss
          </button>
        </div>

        {/* Decorative corner elements */}
        <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 opacity-30" style={{ borderColor: accentColor }} />
        <div className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 opacity-30" style={{ borderColor: accentColor }} />
        <div className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 opacity-30" style={{ borderColor: accentColor }} />
        <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 opacity-30" style={{ borderColor: accentColor }} />
      </div>
    </div>
  );
};
